import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, ExternalLink, FlaskConical } from "lucide-react";
import api from "../lib/api";
import { Seo, siteOrigin } from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Cited, Paragraphs, SectionHeading, ShopBox } from "../components/guide/GuideBits";

const TOC = [
  ["what", "What it is"], ["areas", "Research areas"], ["mechanism", "How it works"],
  ["evidence", "Evidence"], ["faq", "FAQ"], ["glossary", "Glossary"], ["references", "References"],
];

const buildJsonLd = (g, origin) => {
  const url = `${origin}/api/g/${g.slug}`;
  const interactive = `${origin}/guides/${g.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article", "@id": `${url}#article`, headline: `${g.peptide} — Research Guide`, url, mainEntityOfPage: url, sameAs: interactive,
        description: g.quick_answer, inLanguage: "en-CA",
        author: { "@type": "Organization", name: g.attribution },
        publisher: { "@type": "Organization", name: "Eternal Biolabs", url: origin },
        datePublished: g.published_at || g.created_at, dateModified: g.updated_at,
        about: { "@type": "Thing", name: g.peptide },
        citation: g.references.map((r) => ({ "@type": "CreativeWork", name: r.title, url: r.url })),
      },
      {
        "@type": "FAQPage", "@id": `${url}#faq`,
        mainEntity: g.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Research", item: `${origin}/research` },
          { "@type": "ListItem", position: 2, name: "Peptide Guides", item: `${origin}/research#guides` },
          { "@type": "ListItem", position: 3, name: g.peptide, item: url },
        ],
      },
    ],
  };
};

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setGuide(null);
    setMissing(false);
    api.get(`/guides/${slug}`).then(({ data }) => setGuide(data)).catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (missing) {
    return (
      <div className="relative z-10 max-w-md mx-auto px-6 py-32 text-center" data-testid="guide-not-found">
        <h1 className="font-display text-xl uppercase tracking-wide text-[#1A1A1A]">Guide not found</h1>
        <Link to="/research#guides" className="mt-6 inline-block text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B]">Browse all guides →</Link>
      </div>
    );
  }
  if (!guide) {
    return (
      <div className="relative z-10 flex justify-center py-40">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="guide-page">
      <Seo
        title={`${guide.peptide} Research Guide — What It Is, Research Areas & Evidence`}
        description={guide.quick_answer.slice(0, 300)}
        path={`/guides/${guide.slug}`}
        canonical={`${siteOrigin()}/api/g/${guide.slug}`}
        jsonLd={buildJsonLd(guide, siteOrigin())}
      />

      <nav className="flex items-center gap-2 text-xs text-[#6E675F] mb-10 flex-wrap" aria-label="Breadcrumb" data-testid="guide-breadcrumb">
        <Link to="/research" className="inline-flex items-center gap-1.5 hover:text-[#1A1A1A] transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Research</Link>
        <span>›</span>
        <Link to="/research#guides" className="hover:text-[#1A1A1A] transition-colors">Peptide Guides</Link>
        <span>›</span>
        <span className="text-[#1A1A1A] font-semibold">{guide.peptide}</span>
      </nav>

      <nav className="lg:hidden sticky top-16 z-30 -mx-6 px-6 py-2.5 mb-6 bg-[#F7F5F2]/90 backdrop-blur-xl border-y border-[#DDD7CF]/60 flex gap-2 overflow-x-auto no-scrollbar" aria-label="On this page" data-testid="guide-toc-mobile">
        {TOC.map(([id, label]) => (
          <a key={id} href={`#${id}`} data-testid={`guide-toc-mobile-${id}`} className="shrink-0 inline-flex items-center min-h-[36px] text-[11px] tracking-[0.08em] uppercase font-semibold px-3.5 rounded-none border border-[#DDD7CF] bg-white text-[#3D3833] active:bg-[#F3EFE9]">
            {label}
          </a>
        ))}
      </nav>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-16">
        <article className="max-w-3xl">
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Research Guide · {guide.category}
          </p>
          <h1 className="font-display text-2xl sm:text-4xl uppercase tracking-wide text-[#1A1A1A] mt-3 leading-tight" data-testid="guide-title">
            {guide.peptide}
          </h1>
          <p className="text-xs text-[#6E675F] mt-4" data-testid="guide-meta">
            By the {guide.attribution} · Last reviewed {guide.last_reviewed} · {guide.references.length} references
          </p>

          <div className="mt-8 bg-[#F3EFE9] border-l-4 border-[#8C7B6B] rounded-r-lg px-6 py-5" data-testid="guide-quick-answer">
            <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#8C7B6B] mb-2">Quick answer</p>
            <p className="text-base sm:text-lg leading-relaxed text-[#1A1A1A]"><Cited text={guide.quick_answer} /></p>
          </div>

          <div className="lg:hidden mt-8">
            <ShopBox peptide={guide.peptide} products={guide.products} compact />
          </div>

          <section className="mt-14">
            <SectionHeading id="what">What {guide.peptide} is</SectionHeading>
            <div className="space-y-4"><Paragraphs text={guide.what_it_is} /></div>
          </section>

          <section className="mt-14">
            <SectionHeading id="areas">What it is being researched for</SectionHeading>
            <ol className="space-y-6" data-testid="guide-research-areas">
              {guide.research_areas.map((a, i) => (
                <li key={`area-${i}`} className="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-4">
                  <span className="font-display text-sm text-[#8C7B6B] pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-base font-semibold text-[#1A1A1A]">{a.title}</h3>
                    <div className="mt-2 space-y-3"><Paragraphs text={a.body} /></div>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-14">
            <SectionHeading id="mechanism">How it is thought to work</SectionHeading>
            <div className="space-y-4"><Paragraphs text={guide.mechanism} /></div>
          </section>

          <section className="mt-14">
            <SectionHeading id="evidence">Where the evidence stands</SectionHeading>
            <div className="space-y-4"><Paragraphs text={guide.evidence} /></div>
          </section>

          <section className="mt-14">
            <SectionHeading id="faq">Frequently asked questions</SectionHeading>
            <Accordion type="single" collapsible className="border border-[#DDD7CF] rounded-lg bg-white px-5" data-testid="guide-faq">
              {guide.faqs.map((f, i) => (
                <AccordionItem key={`faq-${i}`} value={`faq-${i}`} className="border-[#EFEBE6]">
                  <AccordionTrigger className="text-left text-sm font-semibold text-[#1A1A1A] hover:no-underline py-4" data-testid={`guide-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-[#3D3833] pb-5"><Cited text={f.a} /></AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {guide.glossary.length > 0 && (
            <section className="mt-14">
              <SectionHeading id="glossary">Glossary</SectionHeading>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="guide-glossary">
                {guide.glossary.map((t, i) => (
                  <div key={`term-${i}`} className="border-t border-[#DDD7CF] pt-3">
                    <dt className="text-sm font-semibold text-[#1A1A1A]">{t.term}</dt>
                    <dd className="text-sm text-[#6E675F] mt-1 leading-relaxed">{t.definition}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <section className="mt-14">
            <SectionHeading id="references">References</SectionHeading>
            <ol className="space-y-3" data-testid="guide-references">
              {guide.references.map((r, i) => (
                <li key={`ref-${i}-${r.n}`} id={`ref-${r.n}`} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-2 text-sm scroll-mt-28">
                  <span className="text-[#8C7B6B] font-semibold">[{r.n}]</span>
                  <span className="min-w-0">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] underline underline-offset-2 decoration-[#DDD7CF] hover:decoration-[#8C7B6B] break-words inline-flex items-start gap-1" data-testid={`guide-ref-link-${r.n}`}>
                      {r.title} <ExternalLink className="w-3 h-3 shrink-0 mt-1 text-[#8C7B6B]" />
                    </a>
                    {r.source && <span className="block text-xs text-[#6E675F] mt-0.5">{r.source}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <div className="lg:hidden mt-12">
            <ShopBox peptide={guide.peptide} products={guide.products} compact />
          </div>

          <p className="text-xs text-[#6E675F] mt-14 border-t border-[#DDD7CF] pt-6 flex gap-2" data-testid="guide-notice">
            <FlaskConical className="w-4 h-4 text-[#8C7B6B] shrink-0" /> {guide.notice}
          </p>
        </article>

        <aside className="hidden lg:block">
          <div className="sticky top-28 space-y-6">
            <ShopBox peptide={guide.peptide} products={guide.products} />
            <nav className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid="guide-toc">
              <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#6E675F] mb-3">On this page</p>
              <ul className="space-y-2">
                {TOC.map(([id, label]) => (
                  <li key={id}><a href={`#${id}`} className="text-sm text-[#3D3833] hover:text-[#8C7B6B] transition-colors">{label}</a></li>
                ))}
              </ul>
            </nav>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/g/${guide.slug}`} target="_blank" rel="noopener noreferrer" className="block text-[11px] text-[#6E675F] hover:text-[#8C7B6B] transition-colors px-1" data-testid="guide-plain-html-link">
              Plain-text version for citation →
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
